import Swal from "sweetalert2";
import {
  WR_FLAG_RATIO,
  formatWrRatioAsPercentLabel,
} from "./pondSamplingUpload";
import "./wrFlagConfirm.css";

/**
 * Ask the user to verify length/weight when Wr exceeds WR_FLAG_RATIO.
 *
 * @param {Array<{ index?: number, species?: string, length?: string|number, weight?: string|number, wr: number }>} flaggedRows
 * @param {{
 *   actionVerb?: string,
 *   allowExcludeFlagged?: boolean,
 *   confirmAllText?: string,
 *   excludeFlaggedText?: string,
 * }} [options]
 * @returns {Promise<'cancel'|'exclude_flagged'|'include_flagged'>}
 */
export async function confirmHighWrSamples(flaggedRows, options = {}) {
  if (!flaggedRows?.length) return "include_flagged";

  const {
    actionVerb = "saving",
    allowExcludeFlagged = flaggedRows.length > 1,
    confirmAllText,
    excludeFlaggedText,
  } = options;

  const wrPctThreshold = Math.round(WR_FLAG_RATIO * 100);
  const previewLimit = 12;
  const previewRows = flaggedRows.slice(0, previewLimit);
  const moreCount = flaggedRows.length - previewRows.length;
  const showRowCol = flaggedRows.some((f) => f.index != null);
  const rowWord = flaggedRows.length === 1 ? "row" : "rows";
  const hasWord = flaggedRows.length === 1 ? "has" : "have";
  const isMultiAction = allowExcludeFlagged;

  const listHtml = previewRows
    .map(
      (f) =>
        `<tr>
          ${showRowCol ? `<td>${escapeHtml(f.index)}</td>` : ""}
          <td>${escapeHtml(f.species || "—")}</td>
          <td class="is-num">${escapeHtml(f.length ?? "—")}</td>
          <td class="is-num">${escapeHtml(f.weight ?? "—")}</td>
          <td class="is-num"><span class="wr-flag-modal__wr">${escapeHtml(
            formatWrRatioAsPercentLabel(f.wr)
          )}</span></td>
        </tr>`
    )
    .join("");

  const html = `
    <div class="wr-flag-modal__shell">
      <div class="wr-flag-modal__header">
        <div class="wr-flag-modal__icon" aria-hidden="true">
          <i class="fas fa-exclamation-triangle"></i>
        </div>
        <div class="wr-flag-modal__heading">
          <h3 class="wr-flag-modal__title">Unusually high Wr detected</h3>
          <p class="wr-flag-modal__subtitle">
            <strong>${flaggedRows.length}</strong> ${rowWord} ${hasWord} Wr over ${wrPctThreshold}%.
            Please verify length and weight before ${escapeHtml(actionVerb)}.
          </p>
          <span class="wr-flag-modal__badge">Wr &gt; ${wrPctThreshold}%</span>
        </div>
      </div>
      <div class="wr-flag-modal__body">
        <p class="wr-flag-modal__hint">
          Extreme Wr values are often caused by a mistyped length or weight.
        </p>
        <div class="wr-flag-modal__table-wrap">
          <table class="wr-flag-modal__table">
            <thead>
              <tr>
                ${showRowCol ? `<th>Row</th>` : ""}
                <th>Species</th>
                <th class="is-num">Length</th>
                <th class="is-num">Weight</th>
                <th class="is-num">Wr</th>
              </tr>
            </thead>
            <tbody>${listHtml}</tbody>
          </table>
        </div>
        ${
          moreCount > 0
            ? `<p class="wr-flag-modal__more">…and ${moreCount} more flagged ${
                moreCount === 1 ? "row" : "rows"
              }</p>`
            : ""
        }
      </div>
    </div>
  `;

  const result = await Swal.fire({
    html,
    icon: false,
    title: false,
    showConfirmButton: true,
    showCancelButton: true,
    showDenyButton: isMultiAction,
    reverseButtons: true,
    focusCancel: true,
    buttonsStyling: false,
    heightAuto: false,
    scrollbarPadding: false,
    allowOutsideClick: false,
    confirmButtonText:
      confirmAllText ||
      (isMultiAction ? "Save all including flagged" : "Save anyway"),
    denyButtonText: excludeFlaggedText || "Save only OK rows",
    cancelButtonText: "Cancel",
    customClass: {
      container: `wr-flag-swal${isMultiAction ? " wr-flag-swal--multi" : ""}`,
      popup: "wr-flag-modal",
      htmlContainer: "wr-flag-modal__html",
      actions: "wr-flag-modal__actions",
      confirmButton: "wr-flag-modal__btn wr-flag-modal__btn--primary",
      denyButton: "wr-flag-modal__btn wr-flag-modal__btn--secondary",
      cancelButton: "wr-flag-modal__btn wr-flag-modal__btn--ghost",
    },
    didOpen: (popup) => {
      // Belt-and-suspenders: strip any leftover default Swal chrome.
      popup.querySelectorAll(".swal2-icon, .swal2-title, .swal2-header").forEach((el) => {
        el.style.display = "none";
      });
    },
  });

  if (result.isDismissed) return "cancel";
  if (result.isDenied) return "exclude_flagged";
  return "include_flagged";
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export { WR_FLAG_RATIO, formatWrRatioAsPercentLabel };
